import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import {
  CompanyDetails,
  BasicResponse,
  UpdateVerificationStatusRequest,
} from "../types/api";
import companyService from "../services/company.service";
import { useAuth } from "./AuthContext";

interface ErrorState {
  pendingCompanies: string | null;
  allCompanies: string | null;
  updateCompanyVerificationStatus: string | null;
}

interface LoadingState {
  pendingCompanies: boolean;
  allCompanies: boolean;
  updateCompanyVerificationStatus: boolean;
}

interface AdminContextType {
  pendingCompanies: CompanyDetails[] | null;
  pendingCompaniesCount: number; // New: Count of pending companies
  allCompanies: CompanyDetails[] | null;
  loadingState: LoadingState;
  errorState: ErrorState;
  fetchPendingCompanies: () => Promise<void>;
  fetchAllCompanies: () => Promise<void>;
  updateCompanyVerificationStatus: (
    companyId: string,
    reqBody: UpdateVerificationStatusRequest,
  ) => Promise<boolean>;
}

const AdminContext = createContext<AdminContextType | undefined>(undefined);

export const AdminProvider = ({ children }: { children: ReactNode }) => {
  const [pendingCompanies, setPendingCompanies] = useState<
    CompanyDetails[] | null
  >(null);
  const [pendingCompaniesCount, setPendingCompaniesCount] = useState<number>(0); // New state for pending companies count
  const [allCompanies, setAllCompanies] = useState<CompanyDetails[] | null>(
    null
  );
  const { user } = useAuth();

  const [loadingState, setLoadingState] = useState<LoadingState>({
    pendingCompanies: false,
    allCompanies: false,
    updateCompanyVerificationStatus: false,
  });

  const [errorState, setErrorState] = useState<ErrorState>({
    pendingCompanies: null,
    allCompanies: null,
    updateCompanyVerificationStatus: null,
  });

  const setLoading = (key: keyof LoadingState, value: boolean) => {
    setLoadingState((prev) => ({ ...prev, [key]: value }));
  };

  const setError = (key: keyof ErrorState, message: string | null) => {
    setErrorState((prev) => ({ ...prev, [key]: message }));
  };

  const fetchPendingCompanies = async () => {
    if (loadingState.pendingCompanies == true) return;
    setLoading("pendingCompanies", true);
    setError("pendingCompanies", null);
    await delay(100);
    try {
      const response: BasicResponse<CompanyDetails[]> =
        await companyService.searchCompanies("");
      if (response.status === "success" && response.data) {
        const filteredPending = response.data.filter(
          (company) => company.verificationStatus === "Pending"
        );
        setPendingCompanies(filteredPending);
        setPendingCompaniesCount(filteredPending.length); // Set pending companies count
      } else {
        setError(
          "pendingCompanies",
          response.status || "Failed to fetch pending companies"
        );
        setPendingCompaniesCount(0); // Set count to 0 on error
      }
    } catch (error: any) {
      console.error("Error fetching pending companies:", error);
      if(typeof error == 'object' && error.data){
        setPendingCompanies([]);
      } else{
      setError(
        "pendingCompanies",
        error.message ||
          "An unexpected error occurred while fetching pending companies"
      );
    }
      setPendingCompaniesCount(0); // Set count to 0 on error
    } finally {
      setLoading("pendingCompanies", false);
    }
  };
  const delay = (ms: number) =>
    new Promise((resolve) => setTimeout(resolve, ms));

  const fetchAllCompanies = async () => {
    if (loadingState.allCompanies == true) return;
    setLoading("allCompanies", true);
    setError("allCompanies", null);
    await delay(100);
    try {
      const response: BasicResponse<CompanyDetails[]> =
        await companyService.searchCompanies("");
      if (response.status === "success" && response.data) {
        setAllCompanies(response.data);
      } else {
        setError(
          "allCompanies",
          response.status || "Failed to fetch all companies"
        );
      }
      setLoading("allCompanies", false);
    } catch (error: any) {
      console.error("Error fetching all companies:", error);
      if(typeof error == 'object' && error.data){
        setAllCompanies([]);
      } else{
        setError(
          "allCompanies",
          error.message ||
          "An unexpected error occurred while fetching all companies"
        );
      }
    } finally {
      setLoading("allCompanies", false);
    }
  };

  const updateCompanyVerificationStatus = async (
    companyId: string,
    reqBody: UpdateVerificationStatusRequest,
  ): Promise<boolean> => {
    setLoading("updateCompanyVerificationStatus", true);
    setError("updateCompanyVerificationStatus", null);
    try {
      const response = await companyService.updateCompanyVerificationStatus(
        companyId,
        reqBody,
      );
      if (response) {
        // Remove the updated company from the pending list
        setPendingCompanies((prev) =>
          prev
            ? prev.filter((company) => company.companyId !== companyId)
            : null
        );
        const companyDetailsResp = await companyService.getCompanyDetails(companyId);
        if (companyDetailsResp.status == 'success' && companyDetailsResp.data) {
          setAllCompanies((prev) =>
          prev
            ? prev.map((company) =>
                company.companyId === companyId
                  ? { ...companyDetailsResp.data, verificationStatus: reqBody.status }
                  : company
              )
            : null
        );
        }
        else{
          setAllCompanies((prev) =>
          prev
            ? prev.map((company) =>
                company.companyId === companyId
                  ? { ...company, verificationStatus: reqBody.status }
                  : company
              )
            : null
        );
        }
        // Update the status in the allCompanies list
        
        return true;
      } else {
        setError(
          "updateCompanyVerificationStatus",
          "Failed to update company verification status"
        );
        return false;
      }
    } catch (error: any) {
      console.error("Error updating company verification status:", error);
      setError(
        "updateCompanyVerificationStatus",
        error.message ||
          "An unexpected error occurred while updating company verification status"
      );
      return false;
    } finally {
      setLoading("updateCompanyVerificationStatus", false);
    }
  };
  let firstRun = true;
  useEffect(() => {
    if (firstRun && user && user.role === "Admin") {
      firstRun = false;
      fetchPendingCompanies();
      fetchAllCompanies();
    }
  }, []);

  return (
    <AdminContext.Provider
      value={{
        pendingCompanies,
        pendingCompaniesCount, // New: Expose pendingCompaniesCount
        allCompanies,
        loadingState,
        errorState,
        fetchPendingCompanies,
        fetchAllCompanies,
        updateCompanyVerificationStatus,
      }}
    >
      {children}
    </AdminContext.Provider>
  );
};

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (context === undefined) {
    throw new Error("useAdmin must be used within an AdminProvider");
  }
  return context;
};
